import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup } from '@angular/forms';
import { ChartCompiledData, ReportComponentType, ResolvedTypography } from '@core/data/types/advanced-report';
import { Dialog } from '@core/material';
import { GridsterConfig, GridsterItem } from 'angular-gridster2';
import { BehaviorSubject, Subject } from "rxjs";
import { ReportIndexEditFormDialog, ReportIndexEditFormDialogData } from '../../report-dialogs/report-index-edit-form/report-index-edit-form.dialog';
import { editModeCheck } from '../../report-view/report-state/advanced-report.state';

@Component({
    selector: 'pv-report-index',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    templateUrl: 'report-index-component.html',
    styleUrls: ['report-index-component.scss']
})
export class ReportIndexComponent implements OnDestroy, OnChanges {

    @Input()
    set compiledData(data: ChartCompiledData) {
        if (this._compiledData$.getValue() !== data) this._compiledData$.next(data);
    }

    @Input()
    indexControl: FormControl;

    @Input()
    typography: ResolvedTypography;

    @Input()
    configControl: FormControl;

    @Input()
    item: GridsterItem;

    @Input()
    editMode: boolean

    @Input()
    optionKey: string;

    @Input()
    reportKey: string;

    @Input()
    ownerOrgKey: string;

    @Input()
    gridsterConfig: GridsterConfig;

    @Input()
    configFormGroup: FormGroup;

    @Output()
    delete = new EventEmitter<boolean>();

    @Output()
    duplicate = new EventEmitter<ReportComponentType>();

    @Output()
    editTypography = new EventEmitter<void>();

    layerIndex = 1;

    get isUnset(): boolean {
        const v = this.indexControl?.value;
        return !v || !v.dataPoints || v.dataPoints.length === 0;
    }

    private _compiledData$ = new BehaviorSubject<ChartCompiledData>(null);
    private _destroy$ = new Subject();

    constructor(
        private _dialog: Dialog,
    ) {}


    ngOnChanges() {
        this.layerIndex = this.item?.layerIndex ? this.item.layerIndex : 1;
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    moveToTop() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.maxLayerIndex,
        });
    }

    moveForward() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.baseLayerIndex,
        });
    }

    moveDown() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.baseLayerIndex,
        });
    }

    moveToBottom() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.maxLayerIndex,
        });
    }

    getChartValue() {
        return this._compiledData$.getValue();
    }

    edit() {
        if (!editModeCheck(this.editMode)) return;

        const data: ReportIndexEditFormDialogData = {
            reportKey: this.reportKey,
            ownerOrgKey: this.ownerOrgKey,
            data: this.indexControl.value,
        }

        this._dialog.open(ReportIndexEditFormDialog, {
            data,
            panelClass: 'reporting-theme',
            width: '720px',
            maxWidth: '95vw',
            autoFocus: false,
        }).afterClosed()
        .subscribe((result) => {
            if (!result.status) return;

            this.indexControl.setValue(result?.data);
            this.indexControl.markAsDirty();
        })
    }

    remove() {
        this._dialog.confirm(
            'Delete Component',
            `Are you sure you want to delete this component?`,
            'Delete',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.delete.emit(true);
        })
    }

    makeDuplicate() {
        this.duplicate.emit(ReportComponentType.INDEX);
    }
}
